import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { UserTable } from "./users-table/user-table";
import { MonthPicker } from "../ui/month-picker";
import { useState } from "react";
import { DateTime } from "luxon";

interface RetentionModalProps {
  open: boolean;
  onClose: () => void;
  retentionData: any;
}

export function RetentionModal({
  open,
  onClose,
  retentionData,
}: RetentionModalProps) {
  const currentDate = DateTime.now();
  const currentMonth = currentDate.month - 1; // Convert to 0-based indexing for JavaScript compatibility
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  let results = retentionData?.data?.retentionData?.results;
  if (results) {
    results = [...results].reverse();
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="ml-[50px] max-h-[90vh] max-w-[90vw] overflow-y-auto bg-white p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h2 className="font-['TWK Lausanne'] mb-2 text-lg font-normal text-gray-400">
              Last 5 Months
            </h2>
            <h3 className="font-['TWK Lausanne'] mb-4 text-2xl font-semibold text-gray-900">
              Average Monthly Retention
            </h3>

            <div className="mb-4 text-3xl text-gray-900">
              {(
                parseFloat(
                  retentionData?.data?.retentionData?.averageRetention,
                ) * 100
              ).toFixed(0)}
              %
            </div>

            <div className="border-t-2 border-dotted border-blue-300 pt-4">
              <div className="mb-4">
                <h4 className="font-['TWK Lausanne'] mb-4 text-lg font-light text-gray-400">
                  Monthly Breakdown
                </h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={results}>
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 14 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="activeTeens"
                        fill="#FF6B00"
                        radius={[0, 0, 4, 4]}
                        stackId="retention"
                        name="Active Teens"
                      />
                      <Bar
                        dataKey="deactivatedTeens"
                        fill="#FFB693"
                        radius={[4, 4, 0, 0]}
                        stackId="retention"
                        name="Deactivated Teens"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-[#FF6B00]"></div>
                    <span>Active Teens</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-[#FFB693]"></div>
                    <span>Deactivated Teens</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ml-auto flex w-[150px] items-center justify-between">
            <MonthPicker
              month={selectedMonth}
              setMonth={setSelectedMonth}
              monthsToShow={5}
              showYear={true}
              className="w-[148px] px-[12px] py-[8px] text-center"
            />
          </div>

          <UserTable
            selectedMonth={selectedMonth}
            availableTabs={[
              { key: "engaged", label: "Engaged", engagementFilter: "engaged" },
              {
                key: "de-activated",
                label: "De-activated",
                statusFilter: "inactive",
              },
            ]}
            defaultPageSize={10}
            showBadgeFilters={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
